import axios from 'axios';
import { uniqueId } from 'lodash';
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    BACKSPLASH,
    BACKSPLASH_ID,
    DEFAULT_CORNER_RADIUS,
    LIST_ITEM,
    UNIT_MEASURE,
    WATERFALL_ID,
} from '@common/constants';
import { ProductType, containsRadiusOut, productTypeSortOrder } from '@common/enums';
import { isCountertopProduct } from '@common/helpers';
import { useShapeArea } from '@common/hooks/useShapeArea/useShapeArea';
import {
    ApplianceType,
    CornerInfo,
    DiagonalCornerModel,
    DrawingAppliance,
    DrawingShape,
    PricedProduct,
    ProductEstimateQuery,
    ProductQty,
} from '@common/models';
import { SaveEstimateItem } from '@common/models/save-estimate-dto';
import { isActualProduct } from '@common/utils';
import { isShapeOversizeIsland } from '@common/utils/areaUtils';
import { ProductFilters, doesProductMatchFilter } from '@common/utils/productFilterUtility';
import { useSmallJobs } from '@core/hooks/useSmallJobs';
import createTypeSafeContext from '@core/utils/createTypeSafeContext';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useCornerRadii } from '@ee-api/corner-radii-api/hooks/useCornerRadii';
import { useSelectCutoutLabor } from '@ee-api/cutout-labor-api/hooks/useCutoutLaborProducts';
import { useDiagonalCorners } from '@ee-api/diagonal-corners-api/useDiagonalCorners';
import { useSelectedEdge } from '@ee-api/edges-api/hooks/useSelectedEdge';
import { useEndCaps } from '@ee-api/endcaps-api/useEndCaps';
import { useEndSplash } from '@ee-api/endsplash-api/useEndSplash';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useMileageTiers } from '@ee-api/mileage-api/useMileageTiers';
import { useLazyFetchProductsPricesQuery } from '@ee-api/priced-products-api/pricedProductsService';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useWaterfallLabor } from '@ee-api/waterfall-labor-api/useWaterfallLabor';
import { DrawingShapeContext, useOptionalProductContext, useProductDetailsContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { selectSelectedChipMinimizers } from '@redux/slices/chipMinimizerSlice';
import { selectSelectedCutouts } from '@redux/slices/cutoutSlice';
import { selectPreventRepricing, setPreventRepricing } from '@redux/slices/drawingShapeSlice';
import { selectSelectedFaucets } from '@redux/slices/faucetSlice';
import { selectSelectedPlumbing } from '@redux/slices/plumbingSlice';
import { resetCountertopAreaQuantity } from '@redux/slices/pricedProductsSlice';
import {
    buildEdgesPrice,
    getAggregatedPricedProducts,
    getPricingEstimate,
    lineItemsFromPricedProducts,
} from '@redux/slices/pricedProductsSlice/pricedProductSlice.utils';
import { selectSelectedTearOuts } from '@redux/slices/tearOutSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import { PriceTotalsReturn, calculateTotalPrice } from '../components/price/price.utils';
import { getChipMinimizersProductQty } from '../components/scrollable-area/ChipMinimizerSection/ChipMinimizerSection.utils';
import { getCutoutsProductQty } from '../components/scrollable-area/CutoutsSection/cutouts.utils';
import { getPlumbingProductQty } from '../components/scrollable-area/Plumbing/plumbing.utils';
import { getCornerRadiusProductIdFromSize } from '../components/scrollable-area/design-countertop/drawer/editableLabel';
import { getFaucetsProductQty } from '../components/scrollable-area/faucets/faucets.utils';
import { getTearOutsProductQty } from '../components/scrollable-area/tear-out-countertop/tear-outs.utils';
import * as PricedProductsUtils from './pricedProductsContext.utils';

export interface PricedProductsContextValue {
    matchingMileageTierAsPricedProduct?: PricedProduct;
    pricedProducts: PricedProduct[];
    setPricedProducts: (pricedProducts: PricedProduct[]) => void;
    resetPricedProducts: () => void;
    resetAllPricing: () => void;
    quoteLineItems: SaveEstimateItem[];
    priceTotals: PriceTotalsReturn;
    pricedProductsWithoutDelivery: PricedProduct[];
    priceTotalsWithoutDelivery: PriceTotalsReturn;

    ///// usePricedProducts legacy /////
    error: boolean;
    ///// END usePricedProducts legacy /////
}

const addProducts = (pricedProducts: unknown[], products: unknown[]) => {
    products.push(...pricedProducts.filter((item) => item !== undefined));
};

/**
 * test if the given corner is one in the removed list - utility function to eliminate deep nesting
 * @param corner the corner to test
 * @param removedCorners array of removedCorners to search
 */
const isCornerRemoved = (corner: CornerInfo, removedCorners: CornerInfo[] | undefined) =>
    removedCorners?.some((removed) => removed.id === corner.id);

/**
 * find the diagonal corner product that matches a given corner - utility function to eliminate deep nesting
 * @param cornerInfo the corner to search for
 * @param diagonalCorners array of diagonal corners to search
 */
const findDiagonalCorner = (cornerInfo: CornerInfo, diagonalCorners: DiagonalCornerModel[]) =>
    diagonalCorners.find((diagonal) => diagonal.productId === cornerInfo.productId);

export const [usePricedProductsContext, PricedProductsContext] = createTypeSafeContext<PricedProductsContextValue>();
const emptyArray = [] satisfies PricedProduct[] | ProductQty[];

export const PricedProductsContextProvider = ({ children }: { children?: ReactNode }): JSX.Element => {
    const dispatch = useAppDispatch();

    const [_pricedProducts, setPricedProducts] = useState<PricedProduct[]>([]);

    const { isSmallJobWithFee, useMinQtyAreaForPricing, smallJobFeeProduct, isSmallJob, smallJobMinimumArea } =
        useSmallJobs();

    const resetPricedProducts = () => {
        setPricedProducts([]);
    };

    const resetAllPricing = () => {
        dispatch(resetCountertopAreaQuantity());
        resetPricedProducts();
    };

    ///// usePricedProducts legacy /////

    const zipCode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedColor } = useSelectedColor();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const preventRepricing = useAppSelector(selectPreventRepricing);
    const { drawingShapes, areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);
    const { productCatalog } = useProductDetailsContext();
    const { selectSinkCutout, cooktopCutout } = useSelectCutoutLabor();
    const { selectedOptionalProducts, getOptionalProductsProductQty } = useOptionalProductContext();

    const [error, setError] = useState(false);
    const { selectedValue: selectedEdge } = useSelectedEdge();

    const selectedTearOuts = useAppSelector(selectSelectedTearOuts);
    const selectedCutouts = useAppSelector(selectSelectedCutouts);
    const selectedChipMinimizers = useAppSelector(selectSelectedChipMinimizers);
    const selectedPlumbing = useAppSelector(selectSelectedPlumbing);

    const { cornerRadii } = useCornerRadii();
    const { diagonalCorners } = useDiagonalCorners();

    const { waterfallLaborFee } = useWaterfallLabor();
    const selectedFaucets = useAppSelector(selectSelectedFaucets);
    const { t } = useTranslation();

    const { matchingMileageTier } = useMileageTiers();
    const { data: endCaps } = useEndCaps();
    const { data: endSplashes } = useEndSplash();

    const {
        selectedMaterialAreaType,
        totalBaseCountertopArea,
        totalBacksplashArea,
        totalWaterfallArea,
        totalWaterfallLaborEdgeLengths,
        totalEndCapLaborEdgeCount,
        totalEndSplashLaborEdgeCount,
    } = useShapeArea();
    const [lazyFetchPricesQuery] = useLazyFetchProductsPricesQuery();

    const matchingEndCap = useMemo(
        () => endCaps.find((endCap) => endCap.qteSubCode === selectedColor?.colorGroup),
        [endCaps, selectedColor?.colorGroup]
    );
    const matchingEndSplash = useMemo(
        () => endSplashes.find((endSplash) => endSplash.qteSubCode === selectedColor?.colorGroup),
        [endSplashes, selectedColor?.colorGroup]
    );

    const oversizeFeeProductForIslands = useMemo(() => {
        const oversizeFeeProducts = Array.from(productCatalog.values()).filter((product) =>
            doesProductMatchFilter(ProductFilters.oversizeFeeForIslands, product)
        );
        return oversizeFeeProducts.length === 1 ? oversizeFeeProducts[0] : undefined;
    }, [productCatalog]);

    const matchingMileageTierAsPricedProduct: PricedProduct | undefined = useMemo(() => {
        return matchingMileageTier
            ? {
                  ...matchingMileageTier,
                  unitPrice: matchingMileageTier.price,
                  unitPriceWithoutDiscount: matchingMileageTier.priceWithoutDiscount ?? undefined,
                  quantity: 1,
                  adjustedQty: 1,
                  productType: ProductType.MILEAGE_TIER,
                  listKey: matchingMileageTier.productId.toString(),
                  priceWithoutDiscount: matchingMileageTier.price,
                  expirationPromotionDate: null,
              }
            : undefined;
    }, [matchingMileageTier]);

    const pricedProducts: PricedProduct[] = useMemo(() => {
        return [
            ..._pricedProducts,
            ...(_pricedProducts.length && matchingMileageTierAsPricedProduct
                ? [matchingMileageTierAsPricedProduct]
                : emptyArray),
        ].toSorted((a, b) => {
            const aIdx = productTypeSortOrder.findIndex((productType) => a.productType === productType);
            const bIdx = productTypeSortOrder.findIndex((productType) => b.productType === productType);
            return aIdx - bIdx;
        });
    }, [_pricedProducts, matchingMileageTierAsPricedProduct]);

    /**
     * Get all the specifically selected (shopping cart like products) and the edges from the estimate
     */
    const getProducts = useCallback(() => {
        if (!selectedColor || !totalBaseCountertopArea) {
            return;
        }

        // start with the primary color and total countertop area
        const products: ProductQty[] = [
            {
                productId: selectedColor.productId,
                quantity: totalBaseCountertopArea,
            },
        ];

        // add all the different edges from drawing shapes
        addProducts(buildEdgesPrice(drawingShapes), products);

        // and all the directly selected items
        addProducts(getTearOutsProductQty(selectedTearOuts), products);
        addProducts(getOptionalProductsProductQty(), products);
        addProducts(getCutoutsProductQty(selectedCutouts), products);
        addProducts(getChipMinimizersProductQty(selectedChipMinimizers), products);
        addProducts(getFaucetsProductQty(selectedFaucets), products);
        addProducts(getPlumbingProductQty(selectedPlumbing), products);

        return products;
    }, [
        totalBaseCountertopArea,
        drawingShapes,
        getOptionalProductsProductQty,
        selectedChipMinimizers,
        selectedColor,
        selectedCutouts,
        selectedFaucets,
        selectedPlumbing,
        selectedTearOuts,
    ]);

    const addProductEstimateQuery = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        async (): Promise<PricedProduct[]> => {
            const { areProductsReadyToQuery } = PricedProductsUtils;
            if (
                !selectedStore ||
                !selectedMaterial ||
                !selectedColor ||
                !areProductsReadyToQuery(selectedMaterial, selectedColor)
            ) {
                return [];
            }

            const products = getProducts();
            const productEstimateQuery: ProductEstimateQuery = {
                storeId: selectedStore.companyId,
                zipCode,
                products: products ?? [],
            };

            drawingShapes.forEach((drawingShape: DrawingShape) => {
                const cornerInfos = Object.values(drawingShape.cornerMap);
                const removedCorners = drawingShape.removedElementsHistory?.edgesAndCorners.flatMap(
                    (item) => item.appliedCorners
                );

                productEstimateQuery.products.push(
                    //Products from corners
                    ...cornerInfos
                        .filter((cornerInfo) => !isCornerRemoved(cornerInfo, removedCorners))
                        .map((cornerInfo) => {
                            const cornerBySize = cornerInfo.diagonalLength
                                ? undefined
                                : getCornerRadiusProductIdFromSize(
                                      cornerInfo.size ?? DEFAULT_CORNER_RADIUS,
                                      cornerRadii
                                  );
                            const diagonalProduct = cornerInfo.diagonalLength
                                ? findDiagonalCorner(cornerInfo, diagonalCorners)
                                : undefined;
                            return PricedProductsUtils.getProductQtyFromCornerInfo(
                                cornerInfo,
                                diagonalProduct,
                                cornerBySize
                            );
                        })
                        .filter((val): val is ProductQty => !!val), //remove any undefined items
                    //Products from edges
                    ...Object.values(drawingShape.drawingEdges)
                        .filter((edge) => containsRadiusOut(edge) && edge.isVisibleInCanvas)
                        .map((edge) => ({
                            productId: getCornerRadiusProductIdFromSize(edge.realRadius!, cornerRadii),
                            quantity: 1,
                        }))
                        .filter(({ productId }) => isActualProduct(productId))
                );

                if (oversizeFeeProductForIslands && isShapeOversizeIsland(drawingShape, selectedMaterialAreaType)) {
                    productEstimateQuery.products.push({
                        productId: oversizeFeeProductForIslands.productId,
                        quantity: 1,
                    });
                }

                drawingShape.appliances.forEach((appliance: DrawingAppliance) => {
                    if (
                        appliance.applianceModel.applianceType === ApplianceType.SINK ||
                        appliance.applianceModel.applianceType === ApplianceType.SINK_LABOR
                    ) {
                        productEstimateQuery.products.push({
                            productId: appliance.applianceModel.productId,
                            quantity: 1,
                        });

                        if (appliance.applianceModel.applianceType === ApplianceType.SINK) {
                            const cutoutLaborProduct = selectSinkCutout({
                                materialType: selectedMaterial?.productLine,
                                sink: appliance.applianceModel,
                                _source: 'pricedProducts',
                            });

                            if (cutoutLaborProduct) {
                                productEstimateQuery.products.push({
                                    productId: cutoutLaborProduct.productId,
                                    quantity: 1,
                                });
                            }
                        }
                    }
                    if (
                        appliance.applianceModel.applianceType === ApplianceType.DISHWASHER ||
                        appliance.applianceModel.applianceType === ApplianceType.REFRIGERATOR
                    ) {
                        productEstimateQuery.products.push({
                            productId: appliance.applianceModel.productId,
                            quantity: 1,
                        });
                    }
                    if (
                        appliance.applianceModel.applianceType === ApplianceType.COOKTOP ||
                        appliance.applianceModel.applianceType === ApplianceType.RANGE
                    ) {
                        if (appliance.applianceModel.price) {
                            productEstimateQuery.products.push({
                                productId: appliance.applianceModel.productId,
                                quantity: 1,
                            });
                        }

                        if (appliance.applianceModel.applianceType === ApplianceType.COOKTOP && cooktopCutout) {
                            productEstimateQuery.products.push({
                                productId: cooktopCutout.productId,
                                quantity: 1,
                            });
                        }
                    }
                });
            });

            if (totalEndCapLaborEdgeCount && matchingEndCap) {
                productEstimateQuery.products.push({
                    productId: matchingEndCap.productId,
                    quantity: totalEndCapLaborEdgeCount,
                });
            }

            if (totalEndSplashLaborEdgeCount && matchingEndSplash) {
                productEstimateQuery.products.push({
                    productId: matchingEndSplash.productId,
                    quantity: totalEndSplashLaborEdgeCount,
                });
            }

            if (totalBacksplashArea) {
                productEstimateQuery.products.push({
                    productId: selectedColor.productId,
                    quantity: totalBacksplashArea,
                    lineItemId: BACKSPLASH_ID,
                });
            }

            if (totalWaterfallArea) {
                productEstimateQuery.products.push({
                    productId: selectedColor.productId,
                    quantity: totalWaterfallArea,
                    lineItemId: WATERFALL_ID,
                });
            }

            if (totalWaterfallLaborEdgeLengths && waterfallLaborFee) {
                productEstimateQuery.products.push({
                    productId: waterfallLaborFee.productId,
                    quantity: totalWaterfallLaborEdgeLengths,
                    lineItemId: waterfallLaborFee.lineItemId,
                });
            }
            // add the small project fee if needed and available
            if (isSmallJobWithFee) {
                productEstimateQuery.products.push({
                    productId: smallJobFeeProduct!.productId, // null test is done with isSmallJobWithFee
                    quantity: 1,
                });
            }

            // At this point, you have all the products in the estimate, but there could be duplicates, so we'll collapse
            /**
             * products = [[productId: 123, quantity: 3], [productId: 456, quantity: 1], [productId: 123, quantity: 1]]
             */

            const reducedProductsQty = productEstimateQuery.products.reduce(
                (accumulator: ProductQty[], currentProductQty: ProductQty) => {
                    const accumulatedProductsQty = [...accumulator];
                    const duplicateProductsQty = accumulatedProductsQty.findIndex(
                        (productQty) =>
                            productQty.productId === currentProductQty.productId && !currentProductQty.lineItemId
                    );

                    if (duplicateProductsQty !== -1) {
                        accumulatedProductsQty[duplicateProductsQty] = {
                            ...accumulatedProductsQty[duplicateProductsQty],
                            quantity:
                                accumulatedProductsQty[duplicateProductsQty].quantity + currentProductQty.quantity,
                        };
                    } else {
                        accumulatedProductsQty.push(currentProductQty);
                    }

                    return accumulatedProductsQty;
                },
                []
            );

            productEstimateQuery.products = reducedProductsQty.filter((product) => product.quantity > 0);

            // all the products and quantities are complete

            const [unknownProductsQuery, completedPricedProducts] = getPricingEstimate({
                productCatalog,
                productEstimateQuery,
                selectedColor,
            });

            unknownProductsQuery.products = unknownProductsQuery.products.filter((productQty) =>
                isActualProduct(productQty.productId)
            );

            if (unknownProductsQuery.products.length) {
                const { data, isError } = await lazyFetchPricesQuery({ data: productEstimateQuery }, true);
                // TODO Error handling...???
                if (!isError && data?.length) {
                    // adjustedQty would be done for products in catalog, and is required at this point
                    const returnedPricedProducts: PricedProduct[] = data.map((pricedProduct: PricedProduct) => {
                        return { ...pricedProduct, adjustedQty: pricedProduct.quantity };
                    });
                    completedPricedProducts.push(...returnedPricedProducts);
                }
            }

            return completedPricedProducts;
        },
        [
            selectedStore,
            selectedMaterial,
            selectedColor,
            getProducts,
            zipCode,
            drawingShapes,
            totalEndCapLaborEdgeCount,
            matchingEndCap,
            totalEndSplashLaborEdgeCount,
            matchingEndSplash,
            totalBacksplashArea,
            totalWaterfallArea,
            totalWaterfallLaborEdgeLengths,
            waterfallLaborFee,
            productCatalog,
            oversizeFeeProductForIslands,
            selectedMaterialAreaType,
            cornerRadii,
            diagonalCorners,
            selectSinkCutout,
            cooktopCutout,
            lazyFetchPricesQuery,
            isSmallJobWithFee,
            smallJobFeeProduct,
        ]
    );

    const getPricedProducts = useCallback(
        // eslint-disable-next-line sonarjs/cognitive-complexity
        async () => {
            try {
                if (areThereShapesInTheDrawingArea && totalBaseCountertopArea) {
                    setError(false);

                    const pricedProducts = await addProductEstimateQuery();

                    pricedProducts.forEach((pricedProduct) => {
                        pricedProduct.listKey = `${LIST_ITEM}_${uniqueId()}`;

                        // NOTE : This overrides the unit of measure for any of the SLAB products because
                        // they're "ea" in the database and need to be "sq ft" here.
                        if (pricedProduct.productId === selectedColor?.productId) {
                            pricedProduct.unitOfMeasure = UNIT_MEASURE.MATERIALS;
                        }
                        // rename backsplashes and waterfalls so they're not the same as the main countertop
                        if (pricedProduct.lineItemId) {
                            let substituteName;
                            switch (pricedProduct.lineItemId) {
                                case BACKSPLASH_ID:
                                    substituteName = BACKSPLASH;
                                    break;
                                case WATERFALL_ID:
                                    substituteName = t('WATERFALL.TITLE');
                                    break;
                            }
                            if (substituteName) {
                                pricedProduct.name = substituteName;
                                pricedProduct.consumerDesc = substituteName;
                                pricedProduct.abbrevDesc = substituteName;
                            }
                        }
                    });

                    const aggregatedPricedProducts = getAggregatedPricedProducts({
                        drawingShapes,
                        pricedProducts,
                        selectedColor,
                        selectedMaterial,
                    });

                    setPricedProducts(aggregatedPricedProducts);
                } else if (_pricedProducts.length) {
                    resetPricedProducts();
                }
            } catch (error) {
                if (axios.isCancel(error)) {
                    return [];
                }
                console.error(error);

                setError(true);
            }

            return [];
        },
        [
            _pricedProducts.length,
            addProductEstimateQuery,
            areThereShapesInTheDrawingArea,
            totalBaseCountertopArea,
            drawingShapes,
            selectedColor,
            selectedMaterial,
            t,
        ]
    );

    /**
     * Total area of all countertop products in the drawing area, including backsplash and waterfall areas
     */
    // const totalCountertopArea = useMemo(
    //     () =>
    //         pricedProducts.reduce((acc, currentProduct) => {
    //             if (isCountertopProduct(currentProduct)) {
    //                 return acc + currentProduct.quantity;
    //             }
    //             return acc;
    //         }, 0),
    //     [pricedProducts]
    // );

    /**
     * Priced products with delivery products removed (i.e. any mileage tier)
     * used on summary page
     */
    const pricedProductsWithoutDelivery = useMemo(() => {
        return pricedProducts.filter((pricedProduct) => pricedProduct.productType !== ProductType.MILEAGE_TIER);
    }, [pricedProducts]);

    /**
     * price totals for the product excluding delivery
     * used on summary page
     */
    const priceTotalsWithoutDelivery = useMemo(
        () =>
            calculateTotalPrice(
                pricedProductsWithoutDelivery,
                selectedColor,
                isSmallJob,
                useMinQtyAreaForPricing,
                smallJobMinimumArea
            ),
        [pricedProductsWithoutDelivery, selectedColor, selectedMaterial, useMinQtyAreaForPricing]
    );

    /**
     * price totals for all products, including delivery
     */
    const priceTotals: PriceTotalsReturn = useMemo(
        () =>
            calculateTotalPrice(
                pricedProducts,
                selectedColor,
                isSmallJob,
                useMinQtyAreaForPricing,
                smallJobMinimumArea
            ),
        [isSmallJob, pricedProducts, selectedColor, smallJobMinimumArea, useMinQtyAreaForPricing]
    );

    const quoteLineItems = useMemo(() => {
        const counterTopProduct = pricedProducts.find((product) => isCountertopProduct(product));
        return lineItemsFromPricedProducts(pricedProducts).map((item) => {
            return {
                ...item,
                quantity:
                    item.productId === counterTopProduct?.productId && useMinQtyAreaForPricing
                        ? Math.max(item.quantity, smallJobMinimumArea)
                        : item.quantity,
            } as SaveEstimateItem;
        });
    }, [pricedProducts, useMinQtyAreaForPricing, smallJobMinimumArea]);

    useEffect(() => {
        if (preventRepricing) {
            dispatch(setPreventRepricing(false));
            return;
        }
        getPricedProducts();
    }, [
        dispatch,
        drawingShapes,
        getPricedProducts,
        totalBaseCountertopArea,
        selectedColor,
        selectedEdge,
        totalBacksplashArea,
        selectedTearOuts,
        selectedCutouts,
        selectedPlumbing,
        totalWaterfallArea,
        selectedChipMinimizers,
        totalWaterfallLaborEdgeLengths,
        selectedFaucets,
        selectedOptionalProducts,
        productCatalog,
        preventRepricing,
    ]);

    const pricedProductsValue: PricedProductsContextValue = {
        pricedProducts,
        matchingMileageTierAsPricedProduct,
        setPricedProducts,
        resetPricedProducts,
        resetAllPricing,
        quoteLineItems,
        priceTotals,
        ///// usePricedProducts legacy /////
        error,
        pricedProductsWithoutDelivery,
        priceTotalsWithoutDelivery,
    };

    return <PricedProductsContext.Provider value={pricedProductsValue}>{children}</PricedProductsContext.Provider>;
};
